import { Directive, ElementRef, EventEmitter, Input, NgIterable, OnDestroy, Output, Renderer2 } from '@angular/core';
import { AbstractListView } from '.';
import { SortCompareFn } from '../components/sort-field/sort-field.component';

export type FilterFieldMode = '=' | '!=' | '*' | '!*' | '>=' | '<' | '~*' | 'empty' | 'not empty' | 'contains' ;

export type FilterFieldConfig = { value: string, mode: FilterFieldMode }

export type FitlerFieldValueType = undefined | 'numeric';

export const getMatcherForPattern = ( pattern: string, matchMode: FilterFieldMode, valueType?: FitlerFieldValueType) => {
  pattern = pattern?.toLowerCase()
  let matchFn : (v:string) => boolean = null
  switch (matchMode) {
    case '=':
      matchFn = (v)=> pattern == v?.toLowerCase()
      break;
    case '!=':
      matchFn = (v)=> pattern != v?.toLowerCase()
      break;
    case '*': {
      let strRe = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/[*]/g,'.*')
      const re = new RegExp('^'+strRe+'$','i')
      matchFn = (v) => re.test(v)
      break;
    }
    case '!*': {
      let strRe = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/[*]/g,'.*')
      const re = new RegExp('^'+strRe+'$','i')
      matchFn = (v) => !re.test(v)
      break;
    }
    case '~*': {
      const re = new RegExp(pattern,'i')
      matchFn = (v) => re.test(v)
      break;
    }
    case '>=': {
      matchFn = valueType === 'numeric' ?
      (v) => Number(v) >= Number(pattern) : (v) => SortCompareFn(v?.toLowerCase()||'',pattern) >= 0
      break;
    }
    case '<': {
      matchFn = valueType === 'numeric' ?
      (v) => Number(v) < Number(pattern) :  (v) => SortCompareFn(v?.toLowerCase()||'',pattern) < 0
      break;
    }
    case 'empty':
      matchFn = (v) => !v
      break
    case 'not empty':
      matchFn = (v) => !!v
      break;
    case 'contains':
    default:
      matchFn = (v) => {
        if (!v && !pattern) {
          return false;
        }
        const valueAsString = typeof v === 'string' ? v.toLowerCase() : `${v}`.toLowerCase();
        return valueAsString.indexOf(pattern.toLowerCase()) !== -1;
      }
  }
  return matchFn
}
//
// Must be used inside the scope of list-view.directive
//
// currently only support for input[text] form fields.. type in the input element and the
// list gets filtered
//
@Directive({
  selector: 'input[fesFilterField]'
})
export class FilterFieldDirective<T,U extends NgIterable<T>> implements OnDestroy {

  @Input('fesFilterField') public name = '';

  @Input('transformFn') public transformFn : ( v : string ) => string = (v:string) => v?.toLowerCase()

  // How to get the value from a record and key for non-trivial objects (ex. nested property inside RxDocument)
  @Input() valueResolver : ( rec: T, recKey: string|string[] ) => any

  @Input('mode') set modeSetter(mode: FilterFieldMode) {
    this.setMode(mode)
  }

  @Input('filterType') valueType: FitlerFieldValueType;

  @Output('valueChange')
  public valueEmitter = new EventEmitter<string|FilterFieldConfig>()

  mode: FilterFieldMode = 'contains'
  mode$ = new EventEmitter<FilterFieldMode>()

  pattern = ''
  valueIsConfig = false

  matchFn: ( value: string ) => boolean

  @Input('value') set setValue( v : string | FilterFieldConfig) {
    this.valueIsConfig = (v instanceof Object);
    if (this.valueIsConfig ) {
      const config = v as FilterFieldConfig
      this.pattern = config.value
      if ( config.mode ) {
        this.mode = config.mode
      }
    } else {
      this.pattern = v as string
    }
    if ( v != undefined && this.elementRef?.nativeElement ) {
      this.elementRef.nativeElement.value = this.pattern
    }
  }

  constructor(private listView : AbstractListView<T,U>, private elementRef: ElementRef, renderer: Renderer2) {
    this.listView.addFilterField(this)
    renderer.listen(elementRef.nativeElement,'change', this.filterEdited)
    renderer.listen(elementRef.nativeElement,'keyup', this.filterEdited)
  }

  setMode(mode:FilterFieldMode){
    const bModeChanged = mode != this.mode
    this.mode = mode
    if ( bModeChanged ) {
      this.mode$.emit(mode)
    }
  }

  applyFilter = ( filter : { pattern: string, mode: FilterFieldMode }) => {
    this.setMode(filter.mode)
    this.applyPattern(filter.pattern)
  }

  applyPattern = ( strPattern : string ) => {
    this.pattern = strPattern
    this.elementRef.nativeElement.value = strPattern
    this.valueIsConfig ? this.valueEmitter.emit({value:this.pattern,mode:this.mode}) : this.valueEmitter.emit(this.pattern);
    this.filterList()
  }

  filterEdited = (event:any) => {
    this.pattern = this.elementRef.nativeElement.value;
    this.valueIsConfig ? this.valueEmitter.emit({value:this.pattern,mode:this.mode}) : this.valueEmitter.emit(this.pattern);
    this.filterList()
  }

  filterList() {
    this.listView.filter()
  }

  getNativeInputElement() {
    return this.elementRef.nativeElement;
  }

  isApplicable() {
    return this.pattern || ['empty','not empty'].includes(this.mode)
  }

  prepare() {
    this.matchFn = getMatcherForPattern(this.pattern,this.mode,this.valueType);
  }

  accept = ( resolvedVal: any ) => {
    let srcVal = this.transformFn(resolvedVal?.toString())
    return this.matchFn(srcVal)
  }

  ngOnDestroy() {
    this.listView.removeFilterField(this)
  }
}
